import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import _ from '@lodash';
import axios from 'axios';
import apiEndpoints from 'app/constants/apiEndpoints';
import moment from 'moment-timezone';
import { userLoggedOut } from 'app/auth/store/userSlice';

const CLOSING_EVENTS_ENDPOINT = apiEndpoints.CLOSING_EVENTS;
const { CLOSING_EVENTS_EMPLOYEE_COMPENSATION } = apiEndpoints;

const createDefaultValues = () => {
  // Do not make a constant, because _mergeWith will modify the values.
  return {
    id: 'new',
    loan: {
      closingDate: moment().toDate(),
      closingType: '',
      loanType: '',
      propertyType: '',
      comments: '',
      loanAmount: 0,
      location: '',
      lender: '',
      titleCompany: '',
      loanOfficer: '',
      processor: '',
      leadSource: ''
    },
    borrower: {
      name: '',
      phone: ''
    },
    property: {
      streetAddress: '',
      city: '',
      county: '',
      state: '',
      zipCode: ''
    },
    projectedFees: {
      originationFee: 0,
      discount: 0,
      yieldSpread: 0,
      appraisalCompany: '',
      appraisal: 0,
      creditReport: 0,
      processingFee: 0,
      underwritingFee: 0,
      brokerFee: 0,
      totalFunds: 0
    },
    collectedFees: {
      fundDate: null,
      originationFee: 0,
      discount: 0,
      yieldSpread: 0,
      appraisal: 0,
      creditReport: 0,
      processingFee: 0,
      underwritingFee: 0,
      brokerFee: 0,
      brokerCredit: 0,
      nbrBonus: 0,
      totalFunds: 0,
      commissionRate: 0,
      checks: 0
    },
    encompass: {
      loanNumber: 0
    },
    deductionFees: {
      investorUnderliningFee: 0,
      investorTaxServiceFee: 0,
      accuredInterest: 0,
      daysOnline: 0,
      warehouseFoundingFee: 0,
      reconciliationReportFee: 0,
      collateralDualFee: 0,
      delegatedUnderliningFee: 0,
      loanOfficerConcessions: 0
    },
    payments: []
  };
};

const mergeValue = (key, value, uiObject) => {
  return typeof value === 'object' ? _.mergeWith(value, uiObject[key], (o, s) => (_.isNull(s) ? o : s)) : uiObject[key];
};

const mapClosingEventApiToUI = dto => {
  // Complete default values if missing.
  const defaultValues = createDefaultValues();
  return _.mapValues(defaultValues, (value, key) => {
    return dto[key] ? mergeValue(key, value, dto) : value;
  });
};

export const getClosingEvent = createAsyncThunk('closingEvents/details/getClosingEvent', async params => {
  const { closingEventId } = params;
  // TODO Should validate that closingEventId is a valid int number, or show error.
  const response = await axios.get(CLOSING_EVENTS_ENDPOINT + closingEventId);
  const closingEvent = await response.data.data.closingEvent;
  return mapClosingEventApiToUI(closingEvent);
});

export const getEventCompensationForEmployee = createAsyncThunk(
  'closingEvents/details/getEventCompensationForEmployee',
  async params => {
    const { eventId, userId, userRole } = params;
    const url = CLOSING_EVENTS_EMPLOYEE_COMPENSATION.replace(':eventId', eventId)
      .replace(':userId', userId)
      .replace(':userRole', userRole);
    const response = await axios.get(url);
    return response.data.data.compensation;
  }
);

export const saveClosingEvent = createAsyncThunk('closingEvents/details/saveClosingEvent', async closingEvent => {
  const response = await axios.put(CLOSING_EVENTS_ENDPOINT + closingEvent.id, { closingEvent });
  const result = await response.data.data.closingEvent;
  return mapClosingEventApiToUI(result);
});

export const deleteClosingEvent = createAsyncThunk('closingEvents/details/deleteClosingEvent', async closingEvent => {
  const response = await axios.delete(CLOSING_EVENTS_ENDPOINT + closingEvent.id);
  const result = await response.data.success;
  return result;
});

const closingEventSlice = createSlice({
  name: 'closingEvents/details',
  initialState: null,
  reducers: {
    clearSaved: {
      /* eslint-disable-next-line no-unused-vars */
      reducer: (state, action) => {
        return {
          ...state,
          status: {
            savedSuccessfully: null
          }
        };
      }
    },
    clearState: {
      /* eslint-disable-next-line no-unused-vars */
      reducer: (state, action) => {
        return {
          ...state,
          payload: {},
          status: {}
        };
      }
    }
  },
  extraReducers: {
    /* eslint-disable-next-line no-unused-vars */
    [getClosingEvent.fulfilled]: (state, action) => {
      return {
        ...state,
        payload: {
          ...action.payload
        }
      };
    },
    /* eslint-disable-next-line no-unused-vars */
    [getClosingEvent.rejected]: (state, action) => {
      return {
        status: {
          notFound: true
        }
      };
    },
    [getEventCompensationForEmployee.fulfilled]: (state, action) => {
      return {
        ...state,
        compensations: {
          ...action.payload
        }
      };
    },
    /* eslint-disable-next-line no-unused-vars */
    [saveClosingEvent.fulfilled]: (state, action) => {
      return {
        payload: {
          ...action.payload
        },
        status: {
          savedSuccessfully: true
        }
      };
    },
    /* eslint-disable-next-line no-unused-vars */
    [saveClosingEvent.rejected]: (state, action) => {
      return {
        ...state,
        status: {
          serverError: true,
          savedSuccessfully: false
        }
      };
    },
    /* eslint-disable-next-line no-unused-vars */
    [deleteClosingEvent.fulfilled]: (state, action) => {
      return {
        ...state,
        status: {
          deleted: true
        }
      };
    },
    /* eslint-disable-next-line no-unused-vars */
    [deleteClosingEvent.rejected]: (state, action) => {
      return {
        ...state,
        status: {
          serverError: true,
          deleted: false
        }
      };
    },
    [userLoggedOut]: () => null
  }
});

export const { newClosingEvent, clearSaved, clearState } = closingEventSlice.actions;

export default closingEventSlice.reducer;
